import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './UserProfileAdmin.module.css';

type UserProfileModel = {
  id?: string;
  userId: string;
  name?: string;
  email?: string;
  phone?: string;
  pictureUrl?: string;
  facebookId?: string;
  linkedinId?: string;
  createdAt?: string;
  updatedAt?: string;
};

type StatisticsResponse = {
  totalProfiles?: number;
};

export default function UserProfileAdmin() {
  const [searchParams] = useSearchParams();
  const [userProfile, setUserProfile] = useState<UserProfileModel | null>(null);
  const [originalProfile, setOriginalProfile] = useState<UserProfileModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [totalUsers, setTotalUsers] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(true);

  // Get user ID from URL parameters
  const userId = searchParams.get('id');
  const userEmail = searchParams.get('email');

  // API base URL - should match your user-profile-service
  const getApiBaseUrl = () => {
    const envUrl = import.meta.env.VITE_USER_PROFILE_SERVICE_URL;
    if (envUrl === 'RELATIVE') {
      // Use relative URLs for production with nginx proxy
      return '';
    }
    return envUrl || 'http://localhost:14001';
  };

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    // Load total user count on mount
    loadTotalUserCount();

    if (userId || userEmail) {
      loadUserProfile();
    } else {
      // No identifier provided – initialize a fresh profile for manual creation
      const newProfile: UserProfileModel = {
        userId: '',
        name: '',
        email: '',
        phone: '',
        pictureUrl: '',
        facebookId: '',
        linkedinId: '',
      };
      setUserProfile(newProfile);
      setOriginalProfile(null);
      setError(null);
    }
  }, [userId, userEmail]);

  const loadTotalUserCount = async () => {
    try {
      const url = `${API_BASE_URL}/api/user-profiles/admin/statistics`;
      const response = await fetch(url);

      if (response.ok) {
        const stats = (await response.json()) as StatisticsResponse;
        setTotalUsers(stats.totalProfiles || 0);
      }
    } catch (err: unknown) {
      console.error('Error loading user count:', err);
    } finally {
      setLoadingCount(false);
    }
  };

  useEffect(() => {
    // Check if profile has changes compared to original
    if (originalProfile && userProfile) {
      const hasChanges = JSON.stringify(originalProfile) !== JSON.stringify(userProfile);
      setHasChanges(hasChanges);
    }
  }, [userProfile, originalProfile]);

  const loadUserProfile = async () => {
    // If neither userId nor userEmail is provided, there is nothing to load
    if (!userId && !userEmail) {
      console.log('Skipping user profile fetch – no userId or email provided in URL parameters');
      // Ensure we still have a profile object for manual creation
      if (!userProfile) {
        const newProfile: UserProfileModel = {
          userId: '',
          name: '',
          email: '',
          phone: '',
          pictureUrl: '',
          facebookId: '',
          linkedinId: '',
        };
        setUserProfile(newProfile);
        setOriginalProfile(null);
      }
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let url: string;
      let response: Response;

      if (userId) {
        // Try to fetch by userId first
        url = `${API_BASE_URL}/api/user-profiles?id=${encodeURIComponent(userId)}`;
      } else if (userEmail) {
        // Fetch by email
        url = `${API_BASE_URL}/api/user-profiles/email/${encodeURIComponent(userEmail)}`;
      } else {
        url = `${API_BASE_URL}/api/user-profiles`;
      }

      console.log('Fetching user profile from:', url);
      response = await fetch(url);

      // Check if response is HTML (likely a 404 page)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        console.log('Received HTML response, likely 404 or routing issue');
        if (response.status === 404 || !response.ok) {
          // User not found, create a new profile template
          const newProfile: UserProfileModel = {
            userId: userId || '',
            name: '',
            email: userEmail || '',
            phone: '',
            pictureUrl: '',
            facebookId: '',
            linkedinId: '',
          };
          setUserProfile(newProfile);
          setOriginalProfile(null); // No original since it's new
          // Treat as normal new-profile case rather than an error
          setError(null);
          return;
        }
        throw new Error(
          `API returned HTML instead of JSON. Check if the user-profile-service is running and accessible at: ${API_BASE_URL}`
        );
      }

      if (!response.ok) {
        if (response.status === 404) {
          // User not found, create a new profile template
          const newProfile: UserProfileModel = {
            userId: userId || '',
            name: '',
            email: userEmail || '',
            phone: '',
            pictureUrl: '',
            facebookId: '',
            linkedinId: '',
          };
          setUserProfile(newProfile);
          setOriginalProfile(null); // No original since it's new
          setError('User profile not found. You can create a new profile below.');
          return;
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.text();
          if (errorData) {
            errorMessage += ` - ${errorData}`;
          }
        } catch (e) {
          // Ignore parsing error
        }
        throw new Error(errorMessage);
      }

      const profile = (await response.json()) as UserProfileModel;
      setUserProfile(profile);
      setOriginalProfile(JSON.parse(JSON.stringify(profile)) as UserProfileModel); // Deep copy for comparison
    } catch (err: unknown) {
      console.error('Error loading user profile:', err);
      const e = err as { name?: string; message?: string };
      if (e?.name === 'SyntaxError' && (e?.message || '').includes('Unexpected token')) {
        setError(`API Error: The server returned HTML instead of JSON data. This usually means:
        
1. The user-profile-service is not running
2. The API URL is incorrect: ${API_BASE_URL}
3. There's a routing issue in your backend

Please check that your user-profile-service is running and accessible.`);
      } else {
        setError(`Error loading user profile: ${e?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof UserProfileModel, value: string) => {
    setUserProfile(prev => ({
      ...(prev ?? { userId: '' }),
      [field]: value,
    }));

    // Clear success message when user starts editing
    if (success) {
      setSuccess(null);
    }
  };

  const handleSave = async () => {
    if (!userProfile) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const isNewProfile = !originalProfile;
      const endpoint = isNewProfile
        ? `${API_BASE_URL}/api/user-profiles`
        : `${API_BASE_URL}/api/user-profiles/user/${encodeURIComponent(userProfile.userId)}/upsert`;

      const method = isNewProfile ? 'POST' : 'PUT';

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userProfile),
      });

      if (!response.ok) {
        throw new Error(`Failed to save user profile: ${response.status} ${response.statusText}`);
      }

      const savedProfile = (await response.json()) as UserProfileModel;
      setUserProfile(savedProfile);
      setOriginalProfile(JSON.parse(JSON.stringify(savedProfile)) as UserProfileModel);
      setSuccess(
        isNewProfile ? 'User profile created successfully!' : 'User profile updated successfully!'
      );
      setHasChanges(false);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(`Error saving user profile: ${e?.message || 'Unknown error'}`);
      console.error('Error saving user profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalProfile) {
      setUserProfile(JSON.parse(JSON.stringify(originalProfile)));
    }
    setError(null);
    setSuccess(null);
  };

  const handleDelete = async () => {
    if (!userProfile || !originalProfile) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the profile for ${userProfile.name || userProfile.email || userProfile.userId}? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/user-profiles/user/${encodeURIComponent(userProfile.userId)}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete user profile: ${response.status} ${response.statusText}`);
      }

      setSuccess('User profile deleted successfully!');
      setUserProfile(null);
      setOriginalProfile(null);
      setHasChanges(false);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(`Error deleting user profile: ${e?.message || 'Unknown error'}`);
      console.error('Error deleting user profile:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading user profile...</p>
        </div>
      </div>
    );
  }

  if (error && !userProfile) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Error Loading Profile</h2>
          <p>{error}</p>
          <button onClick={loadUserProfile} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>No Profile Data</h2>
          <p>No user profile information is available.</p>
        </div>
      </div>
    );
  }

  const isNewProfile = !originalProfile;

  return (
    <div className={styles.container}>
      <div className={styles.statsBar}>
        <div className={styles.statItem}>
          <span className={styles.statLabel}>Total Users:</span>
          <span className={styles.statValue}>
            {loadingCount ? '...' : totalUsers !== null ? totalUsers.toLocaleString() : 'N/A'}
          </span>
        </div>
      </div>

      <div className={styles.header}>
        <h1>{isNewProfile ? 'Create New User Profile' : 'Edit User Profile'}</h1>
        <div className={styles.headerActions}>
          {hasChanges && <span className={styles.unsavedChanges}>Unsaved changes</span>}
        </div>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className={styles.successMessage}>
          <p>{success}</p>
        </div>
      )}

      <div className={styles.formContainer}>
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSave();
          }}
        >
          {/* Basic Information Section */}
          <div className={styles.section}>
            <h2>Basic Information</h2>

            <div className={styles.formGroup}>
              <label htmlFor="userId">User ID *</label>
              <input
                type="text"
                id="userId"
                value={userProfile.userId || ''}
                onChange={e => handleInputChange('userId', e.target.value)}
                placeholder="Enter unique user ID"
                required
                disabled={!isNewProfile} // Don't allow editing userId for existing profiles
                className={!isNewProfile ? styles.disabled : ''}
              />
              {!isNewProfile && (
                <small className={styles.helpText}>
                  User ID cannot be changed for existing profiles
                </small>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                value={userProfile.name || ''}
                onChange={e => handleInputChange('name', e.target.value)}
                placeholder="Enter full name"
                maxLength={100}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={userProfile.email || ''}
                onChange={e => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
                maxLength={255}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                value={userProfile.phone || ''}
                onChange={e => handleInputChange('phone', e.target.value)}
                placeholder="Enter phone number"
                maxLength={20}
              />
            </div>
          </div>

          {/* Profile Picture Section */}
          <div className={styles.section}>
            <h2>Profile Picture</h2>

            <div className={styles.formGroup}>
              <label htmlFor="pictureUrl">Picture URL</label>
              <input
                type="url"
                id="pictureUrl"
                value={userProfile.pictureUrl || ''}
                onChange={e => handleInputChange('pictureUrl', e.target.value)}
                placeholder="Enter profile picture URL"
              />
              {userProfile.pictureUrl && (
                <div className={styles.imagePreview}>
                  <img
                    src={userProfile.pictureUrl}
                    alt="Profile preview"
                    onError={e => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Social Media Section */}
          <div className={styles.section}>
            <h2>Social Media</h2>

            <div className={styles.formGroup}>
              <label htmlFor="facebookId">Facebook ID</label>
              <input
                type="text"
                id="facebookId"
                value={userProfile.facebookId || ''}
                onChange={e => handleInputChange('facebookId', e.target.value)}
                placeholder="Enter Facebook ID"
                maxLength={100}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="linkedinId">LinkedIn ID</label>
              <input
                type="text"
                id="linkedinId"
                value={userProfile.linkedinId || ''}
                onChange={e => handleInputChange('linkedinId', e.target.value)}
                placeholder="Enter LinkedIn ID"
                maxLength={100}
              />
            </div>
          </div>

          {/* Metadata Section (Read-only) */}
          {!isNewProfile && (
            <div className={styles.section}>
              <h2>Metadata</h2>

              <div className={styles.formGroup}>
                <label>Database ID</label>
                <input
                  type="text"
                  value={userProfile.id || ''}
                  disabled
                  className={styles.disabled}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Created At</label>
                <input
                  type="text"
                  value={
                    userProfile.createdAt ? new Date(userProfile.createdAt).toLocaleString() : ''
                  }
                  disabled
                  className={styles.disabled}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Updated At</label>
                <input
                  type="text"
                  value={
                    userProfile.updatedAt ? new Date(userProfile.updatedAt).toLocaleString() : ''
                  }
                  disabled
                  className={styles.disabled}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <button type="submit" className={styles.saveButton} disabled={saving || !hasChanges}>
              {saving ? 'Saving...' : isNewProfile ? 'Create Profile' : 'Save Changes'}
            </button>

            {hasChanges && !isNewProfile && (
              <button
                type="button"
                onClick={handleReset}
                className={styles.resetButton}
                disabled={saving}
              >
                Reset Changes
              </button>
            )}

            {!isNewProfile && (
              <button
                type="button"
                onClick={handleDelete}
                className={styles.deleteButton}
                disabled={saving}
              >
                {saving ? 'Deleting...' : 'Delete Profile'}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Debug Information */}
      <div className={styles.debugInfo}>
        <h3>Debug Information</h3>
        <div className={styles.debugGrid}>
          <div className={styles.debugItem}>
            <strong>API Base URL:</strong> {API_BASE_URL || '(relative)'}
          </div>
          <div className={styles.debugItem}>
            <strong>Environment:</strong> {import.meta.env.MODE}
          </div>
          <div className={styles.debugItem}>
            <strong>User ID:</strong> {userId || 'Not provided'}
          </div>
          <div className={styles.debugItem}>
            <strong>User Email:</strong> {userEmail || 'Not provided'}
          </div>
          {userId && (
            <div className={styles.debugItem}>
              <strong>Expected API URL:</strong>{' '}
              {`${API_BASE_URL}/user-profiles/user/${encodeURIComponent(userId)}`}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        <h3>Quick Actions</h3>
        <div className={styles.actionGrid}>
          <a
            href={`/user-profile?id=${userProfile.userId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.quickAction}
          >
            View Public Profile
          </a>
          <a
            href={`/profile/${userProfile.userId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.quickAction}
          >
            View Profile Page
          </a>
          <button onClick={loadUserProfile} className={styles.quickAction} disabled={loading}>
            Refresh Data
          </button>
        </div>
      </div>
    </div>
  );
}
